import React, { useEffect, useState } from 'react'
import { FormContext, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { uniqBy } from 'lodash'
import { Box, Button, Grid, Typography } from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'

import { FieldMenu, SelectedItem } from './components'
import { DialogConsentField, MenuButton } from 'components'

import helpers from 'helpers'

import useConsent from 'hooks/useConsent'
import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import useStyles from './styles'
import theme from 'theme'

const Fields = ({ isShow = false }) => {
  const {
    nextStep,
    toNextStep,
    setToNextStep,
    dataConsentForm,
    setDataConsentForm,
    setIsConsentLoading,
  } = useConsent()
  const {
    DEFAULT_EMAIL_ITEM_PROPS,
    NEW_CONSENT_FORM_DRIVER_STEP_2,
    STANDARD_FIELDS_CONSENT_FORM,
  } = constants.consent
  const { TRANSLATION_CATEGORY } = constants.documents

  const snackbar = useSnackbar()
  const classes = useStyles()

  const [openDialog, setOpenDialog] = useState(false)
  const [selectedField, setSelectedField] = useState({})
  const [customFields, setCustomFields] = useState([
    ...STANDARD_FIELDS_CONSENT_FORM,
  ])
  const [selectedItems, setSelectedItems] = useState([])
  const [submitButtonEdit, setSubmitButtonEdit] = useState(false)

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { handleSubmit, errors, setValue } = formMethods
  const email = selectedItems.find((item) => item.name === 'Email')

  const categoryLabel =
    TRANSLATION_CATEGORY[dataConsentForm?.subtitle] || dataConsentForm?.subtitle

  const handleSwitchChange = (event, item, index) => {
    const required = event.target.checked
    setSelectedItems((selectedItems) =>
      selectedItems.map((selectedItem) => {
        if (selectedItem.name === item.name) {
          return { ...selectedItem, required }
        }

        return selectedItem
      }),
    )

    return {
      subtitle: item.description,
      required,
      order: index,
      label: item.label,
      placeholder: item.placeholder,
    }
  }

  const handleFieldDestroy = (itemToRemove) => {
    setSelectedItems(selectedItems.filter((item) => item !== itemToRemove))
  }

  const handleCloseDialog = () => setOpenDialog(false)

  const handleOpenDialog = (item, isButtonEdit = false) => {
    setSubmitButtonEdit(isButtonEdit)
    setOpenDialog(true)
    setSelectedField(item)
  }

  const handleOpenDialogButton = () => {
    setOpenDialog(true)
    setSubmitButtonEdit(true)
    setSelectedField({
      buttonText: dataConsentForm?.buttonText ?? 'EU AUTORIZO',
    })
  }

  const handleButtonSubmit = (data) => {
    if (selectedField && selectedField.name) {
      const newButtonText = data.buttonText

      setSelectedItems((prevItems) =>
        prevItems.map((item) => {
          if (item.name === selectedField.name) {
            return { ...item, buttonTextNotAuthorized: newButtonText }
          }
          return item
        }),
      )
    } else {
      setDataConsentForm((prev) => ({
        ...prev,
        buttonText: data.buttonText,
      }))
    }
    setOpenDialog(false)
  }

  const handleUp = (index, items) => {
    const item = items[index]
    const itemToDown = items[index - 1]
    items[index - 1] = item
    items[index] = itemToDown
    setSelectedItems(items.map((item, order) => ({ ...item, order })))
  }

  const handleDown = (index, items) => {
    const item = items[index]
    const itemToUp = items[index + 1]
    items[index + 1] = item
    items[index] = itemToUp
    setSelectedItems(items.map((item, order) => ({ ...item, order })))
  }

  const consentFieldSelectedField =
    selectedField?.name === 'Não aceitar consentimento' &&
    !selectedField?.buttonTextNotAuthorized
      ? { ...selectedField, buttonText: 'EU NÃO AUTORIZO' }
      : {
          ...selectedField,
          buttonText:
            selectedField.buttonText || selectedField.buttonTextNotAuthorized,
        }

  const onSubmit = async (data) => {
    setIsConsentLoading(true)

    try {
      const components = data
      const consentItem = selectedItems.find(
        (item) => item.name === 'Não aceitar consentimento',
      )
      const buttonTextNotAuthorized = consentItem?.buttonTextNotAuthorized
      const mergedData = {
        ...dataConsentForm,
        buttonTextNotAuthorized,
        not_authorized_button: !!consentItem,
        components,
      }

      setDataConsentForm(mergedData)

      nextStep()
      setToNextStep(false)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error, false) ||
          error?.error?.message,
        variant: 'error',
      })
    } finally {
      setIsConsentLoading(false)
    }
  }

  useEffect(() => {
    const customText = dataConsentForm?.buttonTextNotAuthorized

    if (dataConsentForm?.components) {
      const items =
        helpers.consent.transformComponents(dataConsentForm.components) || []
      const mappedItems = items.map((item) =>
        item.name === 'Não aceitar consentimento' && customText
          ? { ...item, buttonTextNotAuthorized: customText }
          : item,
      )
      const hasEmail = mappedItems.some((item) => item.name === 'Email')

      setSelectedItems(
        hasEmail ? mappedItems : [DEFAULT_EMAIL_ITEM_PROPS, ...mappedItems],
      )
    } else {
      setSelectedItems((selectedItems) =>
        selectedItems.some((item) => item.name === 'Email')
          ? selectedItems
          : [DEFAULT_EMAIL_ITEM_PROPS, ...selectedItems],
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataConsentForm?.components, dataConsentForm?.buttonTextNotAuthorized])

  useEffect(() => {
    if (toNextStep) {
      handleSubmit(onSubmit)()

      if (errors) setToNextStep(false)
    }
    // eslint-disable-next-line
  }, [toNextStep])

  return (
    <Grid id={NEW_CONSENT_FORM_DRIVER_STEP_2} container spacing={3}>
      <Grid item xs={12} md={6} lg={4} height="100%">
        <Box
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(3)}
          justifyContent="start"
          height="100%"
        >
          <Box>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Componentes
            </Typography>
            <Typography variant="caption">
              Lembre-se de coletar somente os dados estritamente necessários dos
              titulares.
            </Typography>
          </Box>
          <FieldMenu
            setSelectedItems={setSelectedItems}
            selectedItems={selectedItems}
            customFields={uniqBy(customFields, 'name')}
            setCustomFields={setCustomFields}
            disabled={isShow}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6} lg={8}>
        <Box className={classes.consentBox}>
          <Box className={classes.headerBox}>
            <Box py={2} px={4}>
              <Typography variant="subtitle2">
                {dataConsentForm?.title}
              </Typography>
              {dataConsentForm?.subtitle && (
                <Typography variant="caption">{categoryLabel}</Typography>
              )}
            </Box>
          </Box>
          <Box>
            <FormContext {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {selectedItems.map((item, index) => (
                  <SelectedItem
                    key={item?.name + index?.toString()}
                    item={item}
                    order={index}
                    handleDown={handleDown}
                    handleUp={handleUp}
                    handleSwitchChange={handleSwitchChange}
                    handleOpenDialog={handleOpenDialog}
                    selectedItems={selectedItems}
                    handleFieldDestroy={handleFieldDestroy}
                    disabled={isShow}
                  >
                    {item?.name === 'Email' && !email?.required && (
                      <Alert severity="warning">
                        <AlertTitle>
                          Recomendamos manter este campo como obrigatório.
                        </AlertTitle>
                        Sem um endereço de e-mail válido, o usuário não
                        conseguirá visualizar esses recibos de consentimento
                        através do portal de privacidade.
                      </Alert>
                    )}
                  </SelectedItem>
                ))}
                <Box className={classes.attributeBox}>
                  <Box>
                    <Typography variant="subtitle2">
                      Botão enviar formulário
                    </Typography>
                    <Typography variant="caption">Enviar formulário</Typography>
                    <Box marginTop={2}>
                      <Button
                        className={classes.helpCentralButton}
                        variant="contained"
                        size="small"
                      >
                        {dataConsentForm?.buttonText ?? 'EU AUTORIZO'}
                      </Button>
                    </Box>
                  </Box>
                  {!isShow && (
                    <Box>
                      <MenuButton>
                        <Button
                          color="secondary"
                          fullWidth
                          size="small"
                          onClick={handleOpenDialogButton}
                          disabled={isShow}
                        >
                          Editar
                        </Button>
                      </MenuButton>
                    </Box>
                  )}
                </Box>
              </form>
            </FormContext>
          </Box>
        </Box>
      </Grid>
      <DialogConsentField
        open={openDialog}
        onClose={handleCloseDialog}
        action="edit"
        selectedField={consentFieldSelectedField}
        customFields={customFields}
        setCustomFields={setCustomFields}
        selectedItems={selectedItems}
        setValue={setValue}
        setSelectedItems={setSelectedItems}
        submitButtonEdit={submitButtonEdit}
        onButtonSubmit={handleButtonSubmit}
      />
    </Grid>
  )
}

Fields.propTypes = {
  isShow: PropTypes.bool,
}

export default Fields
